import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import InfoCardDossier from '../../InfoCardDossier/InfoCardDossier';
import { PeoplePreview } from '../../../liste';
import ConditionalMessage from './ConditionalMessage';
import { ContactCardProps } from './types';

const ContactCard: React.FunctionComponent<ContactCardProps> = function ({
    contactNom,
    contactPrenom,
    contactUserIdentifier,
    prescripteurContacts = [],
    onContactChange,
    infoCardSx,
    textStyle,
    palette,
    onClickUser,
}) {
    const hasAssignedContact = Boolean(contactPrenom && contactNom);

    const isAssignedContactInPrescripteurList = () => {
        if (!hasAssignedContact) return false;

        return prescripteurContacts.some((contact) => {
            const idMatch =
                contactUserIdentifier &&
                contact.id?.toString() === contactUserIdentifier?.toString();
            const userIdMatch =
                contactUserIdentifier &&
                contact.userIdentifier?.toString() === contactUserIdentifier?.toString();
            const nameMatch =
                contact.prenomContact === contactPrenom && contact.nomContact === contactNom;

            return idMatch || userIdMatch || nameMatch;
        });
    };

    const shouldEnableContactChange = () => {
        if (!onContactChange || prescripteurContacts.length === 0) return false;

        if (!hasAssignedContact && prescripteurContacts.length > 0) return true;

        const contactInList = isAssignedContactInPrescripteurList();

        if (hasAssignedContact && !contactInList) return true;
        if (hasAssignedContact && contactInList && prescripteurContacts.length === 1) return false;
        if (hasAssignedContact && contactInList && prescripteurContacts.length > 1) return true;

        return false;
    };

    const getContactChangeTooltip = (): string => {
        if (prescripteurContacts.length === 0) {
            return 'Aucun contact disponible pour ce prescripteur';
        }

        const contactInList = isAssignedContactInPrescripteurList();
        if (hasAssignedContact && contactInList && prescripteurContacts.length === 1) {
            return 'Aucun autre contact disponible pour ce prescripteur';
        }

        return 'Modifier le contact assigné';
    };

    const canChangeContact = shouldEnableContactChange();
    const handleContactClick = () => {
        if (onContactChange && canChangeContact) {
            onContactChange(prescripteurContacts[0]);
        }
    };

    const contactInList = isAssignedContactInPrescripteurList();
    const contactNotInList = hasAssignedContact && !contactInList;

    const renderContent = () => {
        if (hasAssignedContact) {
            return (
                <>
                    <PeoplePreview
                        firstName={contactPrenom}
                        lastName={contactNom}
                        userIdentifier={contactUserIdentifier}
                        sx={{ margin: '0 10px 0 0' }}
                        onClick={onClickUser}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            sx={textStyle}
                            component="span"
                        >
                            {`${contactPrenom} ${contactNom}`}
                        </Typography>
                        {contactNotInList && (
                            <ConditionalMessage
                                text="Contact externe au prescripteur"
                                type="warning"
                            />
                        )}
                    </Box>
                </>
            );
        }

        if (!hasAssignedContact && prescripteurContacts.length > 0) {
            return (
                <ConditionalMessage
                    text="Aucun contact assigné"
                    type="warning"
                />
            );
        }

        return (
            <Typography
                sx={{
                    ...textStyle,
                    fontStyle: 'italic',
                    color: palette.error.main,
                }}
                component="span"
            >
                Aucun contact associé à ce dossier
            </Typography>
        );
    };

    const cardContent = (
        <InfoCardDossier
            label="Assigné à"
            sx={infoCardSx}
            onClick={canChangeContact ? handleContactClick : undefined}
            actionIcon={
                canChangeContact ? (['fasl', 'pen'] as unknown as IconDefinition) : undefined
            }
        >
            {renderContent()}
        </InfoCardDossier>
    );

    const needsTooltip =
        !canChangeContact &&
        (prescripteurContacts.length === 0 ||
            (hasAssignedContact && contactInList && prescripteurContacts.length === 1));

    if (needsTooltip) {
        return (
            <Tooltip
                title={getContactChangeTooltip()}
                arrow
                placement="top"
            >
                <Box>{cardContent}</Box>
            </Tooltip>
        );
    }

    return cardContent;
};

export default ContactCard;
